/* TanStack Query hooks over the expense-user provisioning routes (/v1/expense/admin/users).
   Provisioning creates/updates an expense_user_profile — the record that turns a CIAM user into a
   PROVISIONED expense user (a manager for approval routing). A user can't submit a report until they
   have a profile with a manager, so this is the onboarding gate. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { expenseFetch } from '@/expense/lib/expense-api';

export interface ExpenseUserProfile {
  userOid: string;
  displayName: string | null;
  email: string | null;
  managerUserOid: string | null;
  phoneE164: string | null;
  expenseSenderEmails: string[];
  approvalThreshold: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProvisionRequest {
  userOid?: string; // omit → self-onboard (caller's oid)
  displayName?: string;
  email?: string;
  managerUserOid?: string;
  phoneE164?: string;
  senderEmails?: string[];
  approvalThreshold?: string;
  active?: boolean;
}

const keys = { list: () => ['expense', 'admin', 'users'] as const };

export function useExpenseUsers() {
  return useQuery({
    queryKey: keys.list(),
    queryFn: () =>
      expenseFetch<ExpenseUserProfile[]>('/v1/expense/admin/users'),
  });
}

/** Provision (create/update) an expense user profile. */
export function useProvisionUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: ProvisionRequest) =>
      expenseFetch<ExpenseUserProfile>('/v1/expense/admin/users', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['expense', 'admin', 'users'] }),
  });
}
